import { useEffect, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { StatusPill, Icon, SectionCard } from "../../../mui";
import { brainTokens } from "../../../design/brainTheme";
import {
  getLibraryDocApi,
  upsertLibraryDocApi,
  type LibraryDocRow,
  type LibraryDocType,
} from "../../../lib/brain-api";
import { freshnessLabel } from "../../../lib/library-freshness";
import { MarkdownDocument, markdownToc } from "../../../components/MarkdownDocument";
import { SandboxedDocViewer } from "../../../components/SandboxedDocViewer";
import { useCorrespondenceTarget } from "../../../correspondence/CorrespondenceProvider";

const c = brainTokens.color;

function isHtmlKey(key?: string | null): boolean {
  return Boolean(key && /\.html?$/i.test(key));
}

export function LibraryDocView({
  docKey,
  createScope,
  onBack,
  onSaved,
}: {
  docKey?: string | null;
  createScope?: string;
  onBack: () => void;
  onSaved: (key: string) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [doc, setDoc] = useState<LibraryDocRow | null>(null);
  const [content, setContent] = useState("");
  const [versions, setVersions] = useState<Array<{ versionId: string; lastModified?: string; isLatest?: boolean }>>([]);
  const [related, setRelated] = useState<Array<{ docKey: string; title: string }>>([]);
  const [versionId, setVersionId] = useState<string | null>(null);
  const [editing, setEditing] = useState(Boolean(createScope));
  const [draft, setDraft] = useState("");
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState<LibraryDocType>("overview");
  const [tagsText, setTagsText] = useState("");
  const [format, setFormat] = useState<"md" | "html">("md");
  const [saving, setSaving] = useState(false);

  // Correspondence for this doc shows in the dock (not a bottom panel).
  useCorrespondenceTarget(docKey ? { source: "library-doc", sourceId: docKey, label: title || docKey } : null);

  useEffect(() => {
    if (!docKey) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getLibraryDocApi(docKey, versionId ?? undefined);
        if (cancelled) return;
        setDoc(data.doc);
        setContent(data.content);
        setDraft(data.content);
        setTitle(data.doc.title);
        setDocType(data.doc.docType);
        setTagsText((data.doc.tags ?? []).join(", "));
        setFormat(isHtmlKey(data.doc.docKey) ? "html" : "md");
        setVersions(data.versions);
        setRelated(data.related.map((r) => ({ docKey: r.docKey, title: r.title })));
      } catch (e) {
        enqueueSnackbar(e instanceof Error ? e.message : "Failed to load doc", { variant: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docKey, versionId, enqueueSnackbar]);

  const htmlMode = isHtmlKey(docKey) || (Boolean(createScope) && format === "html");
  const toc = htmlMode ? [] : markdownToc(editing ? draft : content);
  const scope = createScope || doc?.scope || "canon";

  const save = async () => {
    setSaving(true);
    try {
      const tags = tagsText
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      // ponytail: direct-edit + S3 versions for v1; flip to propose→sign-off via plan-tab machinery later.
      const res = await upsertLibraryDocApi({
        key: docKey || undefined,
        scope,
        title: title || "Untitled",
        type: docType,
        content: draft,
        tags,
        format: createScope && !docKey ? format : undefined,
      });
      enqueueSnackbar("Saved", { variant: "success" });
      setEditing(false);
      onSaved(res.doc.docKey);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Save failed", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    if (!docKey) return;
    try {
      await upsertLibraryDocApi({ key: docKey, scope, title: title || doc?.title, type: docType, verify: true });
      enqueueSnackbar("Verified", { variant: "success" });
      setVersionId(null);
      const data = await getLibraryDocApi(docKey);
      setDoc(data.doc);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Verify failed", { variant: "error" });
    }
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: 3, alignItems: "start" }}>
      <Box sx={{ minWidth: 0 }}>
        <Button startIcon={<Icon name="arrow-left" size={14} />} onClick={onBack} sx={{ mb: 2 }}>
          Back to Library
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
          {!editing && doc && (
            <StatusPill
              tone="neutral"
              label={doc.docType === "how-to" ? "How-to" : doc.docType.toUpperCase()}
            />
          )}
          {editing ? (
            <>
              <TextField size="small" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ minWidth: 240 }} />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select label="Type" value={docType} onChange={(e) => setDocType(e.target.value as LibraryDocType)}>
                  <MenuItem value="overview">Overview</MenuItem>
                  <MenuItem value="sop">SOP</MenuItem>
                  <MenuItem value="runbook">Runbook</MenuItem>
                  <MenuItem value="how-to">How-to</MenuItem>
                </Select>
              </FormControl>
              {createScope && !docKey && (
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Format</InputLabel>
                  <Select label="Format" value={format} onChange={(e) => setFormat(e.target.value as "md" | "html")}>
                    <MenuItem value="md">Markdown</MenuItem>
                    <MenuItem value="html">HTML</MenuItem>
                  </Select>
                </FormControl>
              )}
              <TextField
                size="small"
                label="Tags"
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                placeholder="aws, cognito, rds"
                sx={{ minWidth: 200 }}
                helperText="Comma-separated"
              />
            </>
          ) : (
            <Box component="h1" sx={{ fontSize: 24, fontWeight: 700, m: 0, color: c.ink }}>
              {doc?.title ?? title}
            </Box>
          )}
          <Box sx={{ ml: "auto", display: "flex", gap: 1, flexWrap: "wrap" }}>
            {versions.length > 0 && !editing && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Version</InputLabel>
                <Select
                  label="Version"
                  value={versionId ?? versions.find((v) => v.isLatest)?.versionId ?? ""}
                  onChange={(e) => {
                    const v = String(e.target.value);
                    const latest = versions.find((x) => x.isLatest)?.versionId;
                    setVersionId(v === latest ? null : v);
                  }}
                >
                  {versions.map((v) => (
                    <MenuItem key={v.versionId} value={v.versionId}>
                      {v.isLatest ? "Latest" : v.lastModified?.slice(0, 10) ?? v.versionId.slice(0, 8)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {!editing && docKey && doc?.canEdit && (
              <Button variant="outlined" size="small" onClick={() => void verify()}>
                Verify
              </Button>
            )}
            {!editing && doc?.canEdit && (
              <Button variant="contained" size="small" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            {editing && (
              <>
                <Button size="small" onClick={() => { setEditing(false); setDraft(content); }}>
                  Cancel
                </Button>
                <Button variant="contained" size="small" disabled={saving} onClick={() => void save()}>
                  Save
                </Button>
              </>
            )}
          </Box>
        </Box>
        {doc && !editing && (
          <Box sx={{ fontSize: 12.5, color: c.muted2, mb: 2 }}>
            {doc.owner ? `Owner ${doc.owner} · ` : ""}
            {scope} · {freshnessLabel((doc.freshness as any) ?? "unknown", doc.verifiedAt)}
            {(doc.tags ?? []).length > 0 ? ` · tags: ${(doc.tags ?? []).join(", ")}` : ""}
            {versionId && (
              <Button size="small" sx={{ ml: 1 }} onClick={() => setVersionId(null)}>
                Back to latest
              </Button>
            )}
          </Box>
        )}
        {editing ? (
          <TextField
            multiline
            minRows={18}
            fullWidth
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={htmlMode ? "<!DOCTYPE html>…" : "# Title\n\nWrite the doc in markdown…"}
            sx={{ "& textarea": { fontFamily: brainTokens.font.mono, fontSize: 13.5, lineHeight: 1.55 } }}
          />
        ) : htmlMode ? (
          <SandboxedDocViewer html={content || "<p>Empty document.</p>"} title={doc?.title ?? title} />
        ) : (
          <MarkdownDocument content={content || "_Empty document._"} documentKey={docKey ?? undefined} onOpenFile={onSaved}/>
        )}
      </Box>
      <Box sx={{ position: "sticky", top: 16, display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionCard label="On this page" padding="12px 14px">
          {toc.length === 0 ? (
            <Box sx={{ fontSize: 12.5, color: c.muted2 }}>No headings yet</Box>
          ) : (
            toc.map((t) => (
              <Box
                key={t.slug}
                component="a"
                href={`#${t.slug}`}
                sx={{ display: "block", fontSize: 13, color: c.tealInk, textDecoration: "none", py: 0.4, "&:hover": { textDecoration: "underline" } }}
              >
                {t.title}
              </Box>
            ))
          )}
        </SectionCard>
        {related.length > 0 && (
          <SectionCard label="Related" padding="12px 14px">
            {related.map((r) => (
              <Box key={r.docKey} sx={{ fontSize: 13, color: c.ink2, py: 0.4 }}>
                {r.title}
              </Box>
            ))}
          </SectionCard>
        )}
      </Box>
    </Box>
  );
}
